import {
  AfterContentInit,
  AfterViewInit,
  Component, ContentChildren, ElementRef, EventEmitter, HostListener, Input, NgZone,
  OnDestroy, OnInit, Output, QueryList,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import { FlexibleConnectedPositionStrategy, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { FormControl, ValidationErrors } from '@angular/forms';
import { defer, merge, Observable, Subject } from 'rxjs';
import { TemplatePortal } from '@angular/cdk/portal';
import { debounceTime, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

import { descendant } from '../../../core/functions/descendant';
import { Paginator } from '../../../paginator/models/paginator';
import { OptionComponent } from '../option/option.component';

@Component({
  selector: 'mcrai-select',
  template: `<div class="mcrai-select__input-wrapper" (click)="clickIn()" #input>
  <mcrai-input
    class="mcrai-select__input"
    type="text"
    [control]="internalValueControl"
    [placeholder]="placeholder"
    [errors]="errors"
  ></mcrai-input>
  <div class="mcrai-select__arrow"></div>
</div>

<ng-template #panel>
  <mcrai-panel class="mcrai-select__panel" color="secondary">
    <mcrai-panel-header class="panel__header" *ngIf="showQuery()">
      <mcrai-input type="text" placeholder="VyhledÃ¡vÃ¡nÃ­" [control]="queryControl"></mcrai-input>
    </mcrai-panel-header>
    <mcrai-panel-content class="panel__content">
      <div class="mcrai-select__options">
        <ng-content select="mcrai-option"></ng-content>
      </div>
    </mcrai-panel-content>
    <mcrai-panel-actions class="panel__actions" *ngIf="paginator">
      <mcrai-paginator
        class="mcrai-select__paginator"
        (page)="pageChange.emit($event)"
        [paginator]="paginator"
      ></mcrai-paginator>
    </mcrai-panel-actions>
  </mcrai-panel>
</ng-template>
`,
  styles: [`.mcrai-select{flex:1 1 100%}.mcrai-select__input-wrapper{cursor:pointer;position:relative}.mcrai-select__input{pointer-events:none}.mcrai-select__panel{display:flex;flex-direction:column;border:1px solid}.mcrai-select__arrow{position:absolute;bottom:10px;right:7px}.mcrai-select__input .mcrai-input__element{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;padding-right:25px}.mcrai-select__actions{display:flex;padding:0 20px 10px}.mcrai-select__options{display:flex;flex-direction:column;max-height:225px;overflow-y:auto}.mcrai-select__query{flex:1 50%;margin-right:20px}.mcrai-select__paginator{justify-content:flex-start!important;flex:1 50%}.panel__header{margin-bottom:0;padding-bottom:12px;height:60px}.panel__content{padding:0!important}.panel__actions{margin-top:0;height:60px}`],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  host: {
    '[class.mcrai-select]': 'true'
  }
})
export class SelectComponent implements OnInit, AfterViewInit, OnDestroy, AfterContentInit {
  private _destroy: Subject<null> = new Subject<null>();

  @ViewChild('input') public input: ElementRef<any>;
  @ViewChild('panel') public panel: TemplateRef<any>;

  @Input() placeholder: string;
  @Input() paginator: Paginator;
  @Input() multiple: null | undefined | boolean;
  @Input() errors: string | ValidationErrors;

  @Output() queryChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() pageChange:  EventEmitter<number> = new EventEmitter<number>();

  @ContentChildren(OptionComponent) options: QueryList<OptionComponent> = new QueryList<OptionComponent>();

  selectionChange: Observable<any> = defer(() => {
    if (this.options) {
      return merge(...this.options.map(option => option.selectionEvent));
    }

    return this._ngZone.onStable
      .asObservable()
      .pipe(
        take(1),
        switchMap(() => this.selectionChange)
      );
  });

  private _overlayRef: OverlayRef;

  private _externalValueControl: FormControl = new FormControl();
  @Input() set control(value: FormControl) {
    this._externalValueControl = value;
  }

  private _internalValueControl: FormControl = new FormControl();
  get internalValueControl(): FormControl {
    return this._internalValueControl;
  }

  private _queryControl: FormControl = new FormControl();
  get queryControl(): FormControl {
    return this._queryControl;
  }

  constructor(
    private _elRef: ElementRef,
    private _viewRef: ViewContainerRef,
    private _overlay: Overlay,
    private _ngZone: NgZone
  ) { }

  ngOnInit() {
    this._queryControl.valueChanges
      .pipe(
        takeUntil(this._destroy),
        debounceTime(300)
      )
      .subscribe((value: string) => this.queryChange.emit(value));

    this._externalValueControl.valueChanges
      .pipe(
        takeUntil(this._destroy)
      )
      .subscribe(value => {
        const internalValue = [];
        const externalValue = Array.isArray(value) ? value : [value];

        if (!value) {
          this._externalValueControl.markAsUntouched();
          this._externalValueControl.markAsPristine();
        } else {
          this._externalValueControl.markAsTouched();
          this._externalValueControl.markAsDirty();
        }

        this.options.forEach(option => {
          option.isActive = externalValue.indexOf(option.value) !== -1;

          if (option.isActive) {
            internalValue.push(option.internalValue);
          }
        });

        this._internalValueControl
          .setValue(this.isMultiple() ? internalValue.join(', ') : internalValue[0]);
      })
  }

  ngAfterViewInit() { }

  ngAfterContentInit() {
    this.options.changes
      .pipe(
        startWith(null),
        takeUntil(this._destroy)
      )
      .subscribe(() => {
        this._resetOptions();
      });
  }

  ngOnDestroy() {
    this._destroy.next();
  }

  private _resetOptions(): void {
    const changedOrDestroyed = merge(this.options.changes, this._destroy);

    this.selectionChange
      .pipe(
        takeUntil(changedOrDestroyed)
      )
      .subscribe((option: OptionComponent) => {
        if (this.isMultiple()) {
          const values = this._toggleValue(this._externalValueControl.value, option.externalValue);

          this._externalValueControl.setValue(values);
        } else {
          this._externalValueControl.setValue(option.value);
          this._detachOptions();
        }
      });
  }

  private _getPositionStrategy(): FlexibleConnectedPositionStrategy {
    return this._overlay
      .position()
      .flexibleConnectedTo(this.input)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', weight: 1 },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', weight: 2 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', weight: 3 },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', weight: 4 }
      ]);
  }

  private _getConfig(): OverlayConfig {
    return new OverlayConfig({
      positionStrategy: this._getPositionStrategy(),
      minWidth: this.input.nativeElement.clientWidth,
      maxWidth: this.input.nativeElement.clientWidth + 100,
      scrollStrategy: this._overlay.scrollStrategies.reposition()
    });
  }

  private _getPortal(templateRef: TemplateRef<any>): TemplatePortal<any> {
    return new TemplatePortal(templateRef, this._viewRef);
  }

  private _attachOptions(): void {
    const config = this._getConfig();
    const portal = this._getPortal(this.panel);

    if (!this._overlayRef) {
      this._overlayRef = this._overlay.create(config);
    }

    if (!this._overlayRef.hasAttached()) {
      this._overlayRef.attach(portal);
    }
  }

  private _detachOptions(): void {
    if (this._overlayRef) {
      this._overlayRef.detach();
    }
  }

  public clickIn(): void {
    this._attachOptions();
  }

  @HostListener('document:click', ['$event', '$event.target'])
  public clickOut(event: MouseEvent, targetElement: HTMLElement): void {
    // skip check when select-box is not opened
    if (!this._overlayRef || !this._overlayRef.hasAttached()) {
      return;
    }

    const isTargetDescendant = descendant.call(this._overlayRef.overlayElement, targetElement);
    const isHostElement      = this._elRef.nativeElement.contains(targetElement);

    if (!isTargetDescendant && !isHostElement) {
      this._detachOptions();
    }
  }

  private _toggleValue(values: string[], value: string): string[] {
    if (!Array.isArray(values)) {
      return [value];
    }

    const filtered = values.filter(_value => _value !== value);

    return values.length === filtered.length ? values.concat(value) : filtered;
  }

  public isMultiple(): boolean {
    if (this.multiple) {
      return true;
    }

    return typeof this.multiple !== 'undefined';
  }

  public showQuery(): boolean {
    return this.queryChange.observers.length > 0;
  }

  public isActive(value: string): boolean {
    const values = Array.isArray(this.control.value) ? this.control.value : [this.control.value];

    return !!values.find(_value => _value === value);
  }

}
